// filters/transactions.schema.ts
import {
  booleanField,
  dateField,
  enumField,
  numberField,
  recordOfStringSetsField,
  stringField,
  stringSetField,
} from 'ext-ui/hooks/filters/fieldHelpers'
import {
  buildUseFilterOptions,
  FilterSchema,
} from 'ext-ui/hooks/filters/schemaBuilder'
import moment from 'moment'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { OrganizationConnectedPlatform } from 'src/enums'
import {
  TransactionDateType,
  TransactionReceiptStatus,
  TransactionSort,
  TransactionStatus,
} from 'src/models/transaction.models'
import { ExpenseCategoryType } from 'src/queries/organizations/expenseCategories.queries'

export interface TransactionsFilters {
  search?: string
  dateType?: TransactionDateType
  since?: moment.Moment
  until?: moment.Moment
  minClearingBillingCents?: number | undefined
  maxClearingBillingCents?: number | undefined
  status?: Set<string>
  source?: Set<string>
  expenseCategoryStatuses?: Set<string>
  expenseCategories?: Record<string, Set<string>>
  receiptStatus?: Set<string>
  receiptRequired?: boolean
  syncStatuses?: Set<string>
  receiptAttachmentSyncStatuses?: Set<string>
  recipient?: Set<string>
  departments?: Set<string>
  creditCardId?: Set<string>
  mccGroup?: Set<string>
  reviewStatus?: Set<string>
  needsManagerApproval?: boolean
  needsFinalApproval?: boolean
}

export interface TransactionsFiltersBody {
  search?: string
  dateType?: TransactionDateType
  since?: string
  until?: string
  sinceClearedAt?: string
  untilClearedAt?: string
  minClearingBillingCents?: number
  maxClearingBillingCents?: number
  status?: string[]
  source?: string[]
  expenseCategoryStatuses?: string[]
  expenseCategoryLabel?: string[]
  receiptStatus?: string[]
  receiptRequired?: boolean
  receiptMissing?: boolean
  syncStatuses?: string[]
  connectedPlatform?: OrganizationConnectedPlatform
  recipientOrPhysicalCardUserId?: string[]
  departments?: string[]
  creditCardId?: string[]
  virtualCardId?: string[]
  mccGroup?: string[]
  hasAttachment?: boolean
  sort?: string
  missingReceiptOrExpenseCategories?: boolean
  reviewStatus?: string[]
  organizationExpenseReportId?: string
  needsManagerApproval?: boolean
  needsFinalApproval?: boolean
}

export function makeBaseTransactionsSchema(args: {
  // things you already have available at call site
  activeConnectedPlatform?: OrganizationConnectedPlatform
  reportId?: string
  context: TransactionReportType
  userId?: string
  creditCardId?: string
  expenseCategories?: { id: string; type: ExpenseCategoryType }[] | undefined
  sort: TransactionSort
}) {
  let init: Partial<TransactionsFilters> = {}
  switch (args.context) {
    case TransactionReportType.NeedsApproval:
    case TransactionReportType.AccountingTransactions:
    case TransactionReportType.ExpenseReport:
      init = {
        dateType: TransactionDateType.Clear,
        status: new Set([TransactionStatus.Cleared]),
      }
      break
    default:
      break
  }

  const schema: FilterSchema<TransactionsFilters, TransactionsFiltersBody> = {
    fields: {
      search: stringField(init.search),
      dateType: enumField<TransactionDateType>(
        init.dateType ?? TransactionDateType.Transaction,
      ),
      since: dateField(init.since ?? moment().subtract(30, 'days')),
      until: dateField(init.until ?? moment()),
      minClearingBillingCents: numberField(init.minClearingBillingCents),
      maxClearingBillingCents: numberField(init.maxClearingBillingCents),
      status: stringSetField(init.status),
      source: stringSetField(init.source),
      expenseCategoryStatuses: stringSetField(init.expenseCategoryStatuses),
      expenseCategories: recordOfStringSetsField(init.expenseCategories),
      receiptStatus: stringSetField(init.receiptStatus),
      receiptRequired: booleanField(init.receiptRequired),
      syncStatuses: stringSetField(init.syncStatuses),
      recipient: stringSetField(init.recipient),
      departments: stringSetField(init.departments),
      creditCardId: stringSetField(init.creditCardId),
      mccGroup: stringSetField(init.mccGroup),
      reviewStatus: stringSetField(init.reviewStatus),
      receiptAttachmentSyncStatuses: stringSetField(
        init.receiptAttachmentSyncStatuses,
      ),
      needsManagerApproval: booleanField(init.needsManagerApproval),
      needsFinalApproval: booleanField(init.needsFinalApproval),
    },

    // TODO: filter out anything the user shouldn't be able to modify
    // REFACTOR: define these fields based on context
    finalizeUI: (ui) => {
      if([
        TransactionReportType.AccountingTransactions,
        TransactionReportType.ExpenseReport,
        TransactionReportType.NeedsApproval,
      ].includes(args.context)) {
        ui.dateType = TransactionDateType.Clear
        ui.status = new Set([
          TransactionStatus.Cleared,
        ])
      }
      return ui
    },

    finalizeApi: (api, ui) => {
      // Start from field-level mapping that FieldCodecs already produced.
      // Cast to any so we can rename/delete keys before returning typed shape.
      const out: any = { ...(api as any) }

      // status: server expects a default list if not provided
      if (!out.status) {
        out.status = [
          TransactionStatus.Pending,
          TransactionStatus.Cleared,
          TransactionStatus.Declined,
          TransactionStatus.AuthReversal,
        ]
      }

      // recipient (UI) -> recipientOrPhysicalCardUserId (API)
      if (out.recipient) {
        out.recipientOrPhysicalCardUserId = out.recipient
        delete out.recipient
      }

      // receiptMissing derived from the *UI* set (presence of Missing)
      out.receiptMissing = ui.receiptStatus?.has(
        TransactionReceiptStatus.Missing,
      )
        ? true
        : undefined

      // expenseCategoryLabel: flatten UI record-of-sets using external category types
      if (ui.expenseCategories && Object.keys(ui.expenseCategories).length) {
        const vendor = args.expenseCategories?.find(
          (c) => c.type === ExpenseCategoryType.Vendor,
        )
        const customer = args.expenseCategories?.find(
          (c) => c.type === ExpenseCategoryType.Customer,
        )
        const labels = new Set<string>()
        for (const [catId, set] of Object.entries(ui.expenseCategories)) {
          for (const label of set) {
            if (vendor && catId === vendor.id) labels.add(`supp_${label}`)
            else if (customer && catId === customer.id)
              labels.add(`cust_${label}`)
            else labels.add(label)
          }
        }
        if (labels.size) out.expenseCategoryLabel = Array.from(labels)
      }

      // connectedPlatform is present only when syncStatuses has values
      out.connectedPlatform =
        out.syncStatuses && out.syncStatuses.length
          ? (args.activeConnectedPlatform as
              | OrganizationConnectedPlatform
              | undefined)
          : undefined

      out.sort = args.sort

      // Decide the effective dateType (context overlay wins)
      const overlayDateType = [
        TransactionReportType.AccountingTransactions,
        TransactionReportType.ExpenseReport,
        TransactionReportType.NeedsApproval,
      ].includes(args.context)
        ? TransactionDateType.Clear
        : undefined

      const effectiveDateType =
        overlayDateType ?? ui.dateType ?? TransactionDateType.Transaction

      // Build date strings from UI directly (don’t trust earlier routing)
      let sinceStr = ui.since ? ui.since.format('YYYY-MM-DD') : undefined
      let untilStr = ui.until ? ui.until.format('YYYY-MM-DD') : undefined

      // NeedsApproval overrides since to 90 days back (and uses Clear dates)
      if (args.context === TransactionReportType.NeedsApproval) {
        sinceStr = moment().subtract(90, 'days').format('YYYY-MM-DD')
      }

      out.dateType = effectiveDateType

      // Date Type overlays
      switch (effectiveDateType) {
        case TransactionDateType.Transaction:
          out.since = sinceStr
          out.until = untilStr
          delete out.sinceClearedAt
          delete out.untilClearedAt
          break
        case TransactionDateType.Clear:
          out.sinceClearedAt = sinceStr
          out.untilClearedAt = untilStr
          out.sort = TransactionSort.dateClearDesc
          delete out.since
          delete out.until
          break
      }

      // Additional Context overlays
      switch (args.context) {
        case TransactionReportType.AccountingTransactions:
          // Accounting transactions should only ever show cleared
          out.status = [TransactionStatus.Cleared]
          break
        case TransactionReportType.ExpenseReport:
          // Statements should only show cleared transactions
          out.organizationExpenseReportId = args.reportId
          out.status = [TransactionStatus.Cleared]
          break
        case TransactionReportType.NeedsApproval:
          out.needsManagerApproval = true
          out.managerId = args.userId
          break
        case TransactionReportType.Account:
        case TransactionReportType.Budget:
          out.creditCardId = args.creditCardId
            ? [args.creditCardId]
            : out.creditCardId
          break
      }

      return out as TransactionsFiltersBody
    },

    finalizeQuery: (flat, ui) => {
      return flat
    },
  }

  return buildUseFilterOptions<TransactionsFilters, TransactionsFiltersBody>(
    schema,
  )
}
